import { supabase } from '@/integrations/supabase/client';

export interface SearchFilters {
  query?: string;
  location?: string;
  job_type?: string;
  experience_level?: string;
  is_remote?: boolean;
  salary_min?: number;
  sort_by?: 'relevance' | 'newest' | 'salary';
}

export interface JobSearchResult {
  id: string;
  title: string;
  description: string;
  location: string;
  job_type: string;
  experience_level: string;
  salary_min: number | null;
  salary_max: number | null;
  currency: string;
  is_remote: boolean;
  featured: boolean;
  created_at: string;
  company_name: string;
  company_logo_url: string;
  rank_score: number;
}

export const searchJobs = async (
  filters: SearchFilters = {},
  limit: number = 20,
  offset: number = 0
): Promise<{ data: JobSearchResult[] | null; error: any; count?: number }> => {
  try {
    // Use the optimized search_jobs database function
    const { data, error } = await supabase.rpc('search_jobs', {
      search_query: filters.query || '',
      filters: {
        location: filters.location || null,
        job_type: filters.job_type || null,
        experience_level: filters.experience_level || null,
        is_remote: filters.is_remote || null,
        salary_min: filters.salary_min || null,
      },
      sort_by: filters.sort_by || 'relevance',
      search_limit: limit,
      search_offset: offset,
    });

    if (error) {
      console.error('Search jobs error:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Search jobs exception:', error);
    return { data: null, error };
  }
};

export const getJobCounts = async (filters: SearchFilters = {}) => {
  try {
    // Get total count by doing a separate query with the same filters
    let query = supabase
      .from('jobs')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'active');

    // Apply filters similar to search_jobs function
    if (filters.query && filters.query.trim()) {
      query = query.textSearch('fts', filters.query, { type: 'plain', config: 'simple' });
    }
    
    if (filters.location && filters.location !== 'all') {
      query = query.ilike('location', `%${filters.location}%`);
    }
    
    if (filters.job_type && filters.job_type !== 'all') {
      query = query.eq('job_type', filters.job_type);
    }
    
    if (filters.experience_level && filters.experience_level !== 'all') {
      query = query.eq('experience_level', filters.experience_level);
    }
    
    if (filters.is_remote === true) {
      query = query.eq('is_remote', true);
    }
    
    if (filters.salary_min) {
      query = query.gte('salary_max', filters.salary_min);
    }

    const { count, error } = await query;
    return { count: count || 0, error };
  } catch (error) {
    return { count: 0, error };
  }
};

// Cache for recent searches to improve performance
const searchCache = new Map<string, { data: JobSearchResult[]; timestamp: number }>();
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

export const getCachedSearch = (cacheKey: string) => {
  const cached = searchCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

export const setCachedSearch = (cacheKey: string, data: JobSearchResult[]) => {
  searchCache.set(cacheKey, { data, timestamp: Date.now() });
  
  // Cleanup old cache entries
  if (searchCache.size > 50) {
    const oldestKey = Array.from(searchCache.keys())[0];
    searchCache.delete(oldestKey);
  }
};

export const generateCacheKey = (filters: SearchFilters, limit: number, offset: number): string => {
  return JSON.stringify({ filters, limit, offset });
};